import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect } from "react";
import { clsx } from "clsx";
import { Icon } from "../primitives/Icon";
import { AppLogo } from "../brand/AppLogo";
import { ROLE_RANK } from "../../lib/roles";
import { useSidebarStore, isNarrowViewport } from "../../lib/sidebarStore";
import { t } from "../../i18n";
// Resolve via t(), but fall back to the English label baked into nav.ts when
// the key has no entry — t() returns the raw key as a last resort, which we
// detect by string equality.
function tr(key, fallback) {
    if (!key)
        return fallback;
    const v = t(key);
    return v === key ? fallback : v;
}
export function Sidebar({ sections, activeId, role, permissions, onNavigate, brandSubtitle }) {
    const expanded = useSidebarStore((s) => s.expanded);
    const setExpanded = useSidebarStore((s) => s.setExpanded);
    // In narrow-overlay mode, picking a nav item should close the overlay so the
    // page underneath becomes visible. In wide mode the sidebar lives in-flow,
    // so leave the user's preference alone.
    const handleNavigate = useCallback((id) => {
        onNavigate(id);
        if (isNarrowViewport())
            setExpanded(false);
    }, [onNavigate, setExpanded]);
    // Esc closes the overlay in narrow mode.
    useEffect(() => {
        if (!expanded || !isNarrowViewport())
            return;
        const onKey = (e) => {
            if (e.key === "Escape")
                setExpanded(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [expanded, setExpanded]);
    const permSet = permissions ? new Set(permissions) : null;
    const visible = sections
        .filter((s) => ROLE_RANK[role] >= ROLE_RANK[s.role])
        .map((s) => ({
        ...s,
        items: permSet
            ? s.items.filter((item) => permSet.has(`nav.${item.id}`))
            : s.items,
    }))
        .filter((s) => s.items.length > 0);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { id: "sidebar", className: clsx("flex h-full flex-col gap-1 px-1 py-2 lg:gap-1 lg:px-3 lg:py-4", expanded ? "is-expanded" : "is-collapsed"), children: [_jsx("div", { className: "sidebar-header flex items-center px-1 pb-2.5 pt-1 lg:px-2.5", children: _jsx(AppLogo, { size: "md", subtitle: brandSubtitle ?? "Shop operations" }) }), _jsx("nav", { className: "scrollbar flex-1 overflow-auto", children: visible.map((section) => {
                            const sectionLabel = tr(section.titleKey, section.title);
                            return (_jsxs("div", { children: [_jsx("div", { className: "nav-section", children: sectionLabel }), section.items.map((item) => {
                                        const itemLabel = tr(item.labelKey, item.label);
                                        return (_jsxs("button", { type: "button", className: clsx("nav-link w-full cursor-pointer border-0 bg-transparent", item.id === activeId && "active"), onClick: () => handleNavigate(item.id), title: itemLabel, "aria-label": itemLabel, children: [_jsx(Icon, { name: item.icon, size: "md" }), _jsx("span", { className: "nav-label", children: itemLabel })] }, item.id));
                                    })] }, section.title));
                        }) })] }), expanded && (_jsx("button", { type: "button", className: "sidebar-backdrop", "aria-label": "Close menu", onClick: () => setExpanded(false) }))] }));
}
//# sourceMappingURL=Sidebar.js.map